/* ==========================================================================
   WHOVIX motion engine
   Idempotent: safe to load more than once (each section tags the asset).
   ========================================================================== */

(function () {
  'use strict';

  if (window.__whovixMotion) return;
  window.__whovixMotion = true;

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Split headline text into word spans for the mask reveal. */
  function splitWords(root) {
    var targets = root.querySelectorAll('[data-wx-split]:not([data-wx-split-done])');

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-wx-split-done', '');

      if (reduced) return;

      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
      var nodes = [];
      var node;

      while ((node = walker.nextNode())) {
        if (node.nodeValue.trim()) nodes.push(node);
      }

      var index = 0;

      nodes.forEach(function (textNode) {
        var frag = document.createDocumentFragment();
        var words = textNode.nodeValue.split(/(\s+)/);

        words.forEach(function (word) {
          if (!word) return;

          if (!word.trim()) {
            frag.appendChild(document.createTextNode(word));
            return;
          }

          var outer = document.createElement('span');
          outer.className = 'wx-word';

          var inner = document.createElement('span');
          inner.textContent = word;
          inner.style.setProperty('--wx-delay', index * 65 + 'ms');
          index += 1;

          outer.appendChild(inner);
          frag.appendChild(outer);
        });

        textNode.parentNode.replaceChild(frag, textNode);
      });
    });
  }

  /* Reveal on scroll */
  var observer = null;

  function reveal(el) {
    el.classList.add('wx-in');
  }

  function observe(root) {
    var targets = root.querySelectorAll('[data-wx-reveal]:not([data-wx-observed])');

    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (el) {
        el.setAttribute('data-wx-observed', '');
        reveal(el);
      });
      return;
    }

    if (!observer) {
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            reveal(entry.target);
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
      );
    }

    Array.prototype.forEach.call(targets, function (el) {
      el.setAttribute('data-wx-observed', '');

      var box = el.getBoundingClientRect();
      if (box.top < window.innerHeight * 0.88 && box.bottom > 0) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            reveal(el);
          });
        });
        return;
      }

      observer.observe(el);
    });
  }

  /* Stagger children of a group */
  function stagger(root) {
    var groups = root.querySelectorAll('[data-wx-stagger]:not([data-wx-staggered])');

    Array.prototype.forEach.call(groups, function (group) {
      group.setAttribute('data-wx-staggered', '');

      var step = parseInt(group.getAttribute('data-wx-stagger'), 10) || 90;
      var kids = group.querySelectorAll('[data-wx-reveal]');

      Array.prototype.forEach.call(kids, function (kid, i) {
        kid.style.setProperty('--wx-delay', i * step + 'ms');
      });
    });
  }

  /* Marquee - duplicate the track so the loop is seamless */
  function marquee(root) {
    var rails = root.querySelectorAll('[data-wx-marquee]:not([data-wx-marquee-done])');

    Array.prototype.forEach.call(rails, function (rail) {
      rail.setAttribute('data-wx-marquee-done', '');

      var track = rail.querySelector('.wx-marquee__track');
      if (!track || reduced) return;

      var copies = Math.max(2, Math.ceil(rail.offsetWidth / Math.max(track.offsetWidth, 1)) + 1);

      for (var i = 1; i < copies; i++) {
        var clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        rail.appendChild(clone);
      }
    });
  }

  /* Parallax - rAF-throttled, transform only */
  var parallaxItems = [];
  var ticking = false;

  function parallaxFrame() {
    ticking = false;
    var vh = window.innerHeight;

    parallaxItems.forEach(function (item) {
      var box = item.el.getBoundingClientRect();
      if (box.bottom < 0 || box.top > vh) return;

      var progress = (box.top + box.height / 2 - vh / 2) / vh;
      item.el.style.transform =
        'translate3d(0, ' + (progress * item.depth * -100).toFixed(2) + 'px, 0) scale(1.12)';
    });
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(parallaxFrame);
  }

  function parallax(root) {
    if (reduced) return;

    var els = root.querySelectorAll('[data-wx-parallax]:not([data-wx-parallax-done])');
    if (!els.length) return;

    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute('data-wx-parallax-done', '');
      parallaxItems.push({
        el: el,
        depth: parseFloat(el.getAttribute('data-wx-parallax')) || 0.12
      });
    });

    if (parallaxItems.length && !window.__whovixParallaxBound) {
      window.__whovixParallaxBound = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    }

    parallaxFrame();
  }

  /* Count-up stats */
  function counters(root) {
    var nums = root.querySelectorAll('[data-wx-count]:not([data-wx-count-done])');
    if (!nums.length) return;

    Array.prototype.forEach.call(nums, function (el) {
      el.setAttribute('data-wx-count-done', '');

      var target = parseFloat(el.getAttribute('data-wx-count'));
      if (isNaN(target)) return;

      var suffix = el.getAttribute('data-wx-count-suffix') || '';

      if (reduced || !('IntersectionObserver' in window)) {
        el.textContent = target + suffix;
        return;
      }

      el.textContent = '0' + suffix;

      var io = new IntersectionObserver(
        function (entries, self) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.disconnect();

            var start = null;
            var dur = 1400;

            function step(now) {
              if (start === null) start = now;
              var p = Math.min((now - start) / dur, 1);
              var eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
              el.textContent = Math.round(target * eased) + suffix;
              if (p < 1) requestAnimationFrame(step);
            }

            requestAnimationFrame(step);
          });
        },
        { threshold: 0.5 }
      );

      io.observe(el);
    });
  }

  /* Boot */
  function init(root) {
    var scope = root || document;
    splitWords(scope);
    stagger(scope);
    observe(scope);
    marquee(scope);
    parallax(scope);
    counters(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:select', function (event) {
    init(event.target);
  });

  window.whovixMotionInit = init;
})();
